import EverTrue from 'app';
import ErrorLogger from 'entities/helpers/error-log-helper';
import useCallSettings from 'apps/settings/phone-settings/use-call-settings';
import TwilioCallButtonConnected from '../components/twilio-call-button-connected';
import { useIdentity } from 'base/identity-resolver';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';
import { useState, useEffect, useRef } from 'react';
import { trackOutreachAction } from 'apps/outreach/utils/utils';
import { validateNumber, formattedUSPhoneNumber } from 'components/dialer/utils/dialer-utils';
import { establishTwilioDevice, createActionFromCall, createUnconnectedCallAction } from '../utils/twilio-utils';
import useTimer from 'hooks/use-timer';
import { getVoipOrgSettings } from 'apps/settings/phone-settings/use-call-settings-requests';

const useTwilio = () => {
  const [device, setDevice] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [activeCallNotes, setActiveCallNotes] = useState('');
  const activeCallNotesRef = useRef('');
  const activeCallRef = useRef(null);
  const deviceRef = useRef(null);
  const { session } = useIdentity();
  const { callerIdNumber } = useCallSettings();
  const { startTimer, stopTimer, Timer } = useTimer();
  const { flagEnabled: hasSignal, loaded: loadedHasSignal } = useFeatureFlag('signal');

  useEffect(() => {
    activeCallNotesRef.current = activeCallNotes;
  }, [activeCallNotes]);

  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  const isSessionExpired = (session) => {
    if (!session || !session.expire_at) {
      return true;
    }
    return Date.now() > session.expire_at;
  };

  const resetDialer = () => {
    stopTimer();
    setActiveCall(null);
    setActiveCallNotes('');
  };

  useEffect(() => {
    if (!loadedHasSignal || !hasSignal) return undefined;

    let cancelled = false;
    let localDevice = null;

    const setupVoip = async () => {
      try {
        const settings = await getVoipOrgSettings();
        if (cancelled) return;
        if (!settings) {
          if (EverTrue.config.is_development) console.log('[VOIP] Organization does not have VoIP enabled.'); // eslint-disable-line no-console
          return;
        }

        localDevice = await establishTwilioDevice();
        if (cancelled) {
          localDevice?.destroy();
          return;
        }

        deviceRef.current = localDevice;
        setDevice(localDevice);
        if (EverTrue.config.is_development) console.log('[VOIP] Establishing VoIP Device'); // eslint-disable-line no-console
      } catch (error) {
        ErrorLogger.error('VoIP setup failed', { extra: { error, page_name: 'dialer' } });
      }
    };

    setupVoip();

    return () => {
      cancelled = true;
      const toDestroy = deviceRef.current || localDevice;
      if (!toDestroy) return;
      // End an in-flight call before tearing down audio elements — destroying mid-call
      // is what produces the AbortError ("play() interrupted by pause()") and abrupt drops.
      try {
        activeCallRef.current?.disconnect();
      } catch (e) {
        // Twilio may have already torn the call down; ignore.
      }
      toDestroy.destroy();
      deviceRef.current = null;
      setDevice(null);
    };
  }, [hasSignal, loadedHasSignal]);

  const startCall = async (phoneNumber, extension, contactId, onCallDisconnect) => {
    // validations

    if (activeCall) {
      EverTrue.Alert.error('You are already on an active call. You may only have one open call at a time.');
      return;
    }
    if (isSessionExpired(session)) {
      EverTrue.Alert.warn(
        'Unable to connect the call as your session has expired. Please refresh the page or log back in.'
      );
      return;
    }

    if (callerIdNumber) {
      trackOutreachAction('make_phone_call');
      if (device) {
        // Get app_key and org id from application and store since they are preserved even after the session renews
        const { application, token } = session;
        const oid = EverTrue.store.org.get('id');
        const app_key = application?.key;
        const isValid = validateNumber(phoneNumber, true);
        if (isValid) {
          const call = await device.connect({
            params: {
              oid: String(oid),
              app_key,
              auth: token,
              To: formattedUSPhoneNumber(phoneNumber),
              ext: extension,
            },
          });

          // event listeners
          // https://www.twilio.com/docs/voice/sdks/javascript/twiliocall#events
          // answeredAt is set at the 'accept' event (real callee pickup, thanks to
          // <Dial answerOnBridge>), NOT at connect — so recorded call duration is
          // talk time, not talk + ring time. It also doubles as our own record of
          // whether the callee ever answered (checked at disconnect, below), instead
          // of Twilio's undocumented private _wasConnected flag, which is suspected
          // of going stale on some answered calls (SIG-898).
          let answeredAt = null;
          let ringingAt = null;
          let recorded = false;
          call.on('ringing', (hasEarlyMedia) => {
            setActiveCall(call);
            ringingAt = new Date().getTime();
            // Start the visible timer as soon as the call starts ringing, so the agent
            // always sees elapsed time even if 'accept' never fires for this call.
            // Recorded call duration still measures from answeredAt below, unaffected.
            startTimer(ringingAt);
            // Diagnostic for SIG-897 (missing dial tone): hasEarlyMedia tells us whether
            // Twilio ever sent real ringback audio for this call, versus withholding it
            // entirely (e.g. <Dial answerOnBridge>). That distinguishes a backend/TwiML
            // gap from a browser playback issue before committing to either fix. Remove
            // once the root cause is confirmed.
            ErrorLogger.warn('Twilio ringing event received', {
              extra: {
                has_early_media: hasEarlyMedia,
                twilio_call_sid: call?.parameters?.CallSid,
                page_name: 'dialer',
              },
            });
          });

          call.on('accept', (callObject) => {
            // this happens when the callee answers and the call is established
            answeredAt = new Date().getTime();
            setActiveCall(call);
            // Fallback for the rare case a call jumps straight to accept without ever
            // emitting 'ringing' (e.g. immediate pickup / fast bridge) — without this,
            // the timer would never start for that call.
            if (!ringingAt) startTimer(answeredAt);
          });

          // Always give the agent a chance to log the interaction on hangup, whether or
          // not the callee ever answered. Voicemail drops and other legitimate outcomes
          // were being silently skipped when this only fired for a confirmed accept (SIG-898).
          // Recorded call status still reflects reality (SUCCESS only when answeredAt is
          // set; otherwise the caller-supplied unconnectedStatus) so call history doesn't
          // mislabel an unanswered/canceled/failed call as "Connected".
          const openInteractionOnHangup = async (callObject, unconnectedStatus) => {
            if (recorded) return;
            recorded = true;
            const callResponse = answeredAt
              ? await createActionFromCall(callObject, answeredAt, contactId, activeCallNotesRef.current)
              : await createUnconnectedCallAction(callObject, contactId, unconnectedStatus);
            onCallDisconnect({ call: callResponse }); // Function created by the Global Outreach Context. Used to open Quick Interaction Modal
          };

          call.on('cancel', async () => {
            // this happens when the user cancels the call BEFORE anyone answers.
            await openInteractionOnHangup(call, 'CANCELED');
            resetDialer();
          });

          call.on('disconnect', async (callObject) => {
            // this happens when a call ends.
            if (!answeredAt) {
              // A long ring with no accept before disconnect is the same shape a lost
              // 'accept' event would take (SIG-898); log it so we can tell the two apart
              // from an ordinary quick decline/busy signal.
              const ringDurationMs = ringingAt ? new Date().getTime() - ringingAt : 0;
              if (ringDurationMs > 15000) {
                ErrorLogger.error('Twilio call disconnected with no accept after a long ring', {
                  extra: { ringDurationMs, page_name: 'dialer' },
                });
              }
            }
            await openInteractionOnHangup(callObject, 'NO_ANSWER');
            stopTimer();
            setActiveCall(null);
          });

          call.on('reconnecting', (TwilioError) => {
            //this happens when the connectivity was lost and that the SDK is trying to reconnect
            EverTrue.Alert.warn(TwilioError.causes[0], { id: TwilioError.code, timeout: 10000 });
            ErrorLogger.error('Twilio.Call Error', {
              extra: {
                error: TwilioError.code + ':' + TwilioError.description + ' ' + TwilioError.causes[0],
              },
            });
          });

          call.on('reconnected', () => {
            //this happens when the connection was established.
            EverTrue.Alert.success('The call has regained connectivity');
          });

          call.on('warning', function (warningName, warningData) {
            if (warningName === 'low-mos') {
              EverTrue.Alert.warn(
                'We have detected poor call quality conditions. You may experience degraded call quality.'
              );
            }
          });

          call.on('warning-cleared', function (warningName) {
            if (warningName === 'low-mos') {
              EverTrue.Alert.success('Call quality conditions restored');
            }
          });

          call.on('error', async (TwilioError) => {
            // this happens when there is an error with the call. A disconnected/invalid
            // number is a common case here (SIG-875) — the call still happened from the
            // agent's perspective, so it should still open the interaction popup like any
            // other hangup, alongside the technical error alert.
            await openInteractionOnHangup(call, 'FAILED');
            EverTrue.Alert.error(TwilioError.causes[0]);
            ErrorLogger.error('Twilio.Call Error', {
              extra: {
                error: TwilioError.code + ':' + TwilioError.description + ' ' + TwilioError.causes[0],
              },
            });
            resetDialer();
          });
        } else EverTrue.Alert.error('Invalid Phone Number');
      } else {
        EverTrue.Alert.error('Sorry, there was an issue with our Caller integration. Please try again.');
      }
    } else {
      EverTrue.Alert.error(
        'Error: Caller ID is required to make this call. Please set up your caller ID number in settings.',
        {
          actionLabel: 'Set Up Caller ID',
          onAction: () => (window.location.href = '/settings/outreach'),
        }
      );
    }
  };

  const endCall = () => {
    if (device) device.disconnectAll();
    setActiveCall(null);
  };

  return {
    hasVoip: !!device,
    onCall: !!activeCall,
    device,
    activeCall,
    startCall,
    DialerButton: TwilioCallButtonConnected,
    CallTimer: Timer,
    activeCallNotes,
    setActiveCallNotes,
    endCall,
  };
};

export default useTwilio;

//

// IF WE WANT INCOMING CALLS:
// 1. we need to update the outreach service to enable incoming calls (currently disabled).
// 2. Register Device (Only necessary for incoming calls) (twilioDevice.register();)
// 3. add event listener for registered (optional) twilioDevice.on("registered", () => {
//   EverTrue.Alert.success("Your Voice Device is ready to use!");
// });
// 4. add event listener for receiving incoming calls (twilioDevice.on("incoming", handleIncomingCall);)

// IF WE WANT TO ALLOW DIFFERENT AUDIO DEVICES
// twilioDevice.audio.on("deviceChange", updateAllAudioDevices.bind(device));

// // Show audio selection UI if it is supported by the browser.
// if (twilioDevice.audio.isOutputSelectionSupported) {
//   audioSelectionDiv.classList.remove("hide");
// }
